import React, { useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { useAuth } from "react-oidc-context";
import axios from "axios";
import actions from "../store/actions";
import { parseKeycloakProfile } from "../utils/keycloakProfile";
import { makeStyles } from "@material-ui/core/styles";
import reactGA from "react-ga";
import { LOGOUT_FLAG_KEY } from "../utils/authLogout";
import { clearLogoutBroadcast } from "../services/keycloakTokenService";

const useStyles = makeStyles({
  login_view: {
    display: "flex",
    justifyContent: "center",
    margin: 300,
  },
  login_btn: {
    width: 200,
    fontSize: 24,
  },
});

function Login(props) {
  const classes = useStyles();
  const auth = useAuth();
  const { logIn, history, defaulAccess, loggedIn } = props;
  const redirectingToKeycloakRef = useRef(false);

  const logEvent = (user) => {
    reactGA.event({
      category: "Logged In",
      action: `${user} successfully logged in at ${new Date().toString()}`,
    });
  };

  const completeLogin = useCallback(
    (email, username, profile, accessToken) => {
      const isFullAccess =
        (profile.groups || []).includes("operations-admin") ||
        (profile.groups || []).includes("/operations-admin");

      const specificAccess = profile?.operation_access?.length
        ? profile.operation_access
        : [];

      axios
        .get(
          "https://umm0xqw159.execute-api.ap-south-1.amazonaws.com/v1/generate_intugine_user_token_with_keycloak_auth",
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        )
        .then(function (res) {
          try {
            sessionStorage.removeItem(LOGOUT_FLAG_KEY);
          } catch (error) {
            // ignore
          }
          // Drop stale cross-tab logout marker before Redux login so
          // KeycloakTokenManager does not treat this as a remote logout.
          clearLogoutBroadcast();
          logIn({
            token: res.data.token,
            username,
            email,
            profile: profile || {},
          });
          logEvent(username);
          history.push(
            "/home/" +
              (isFullAccess
                ? "Tickets V2"
                : (specificAccess?.length ? specificAccess : defaulAccess)[0])
          );
        })
        .catch(function (error) {
          history.push("/login");
          console.log(error);
          window.location.replace("https://operation.intutrack.com");
        });
    },
    [logIn, history, defaulAccess]
  );

  const hasLogoutFlag = () => {
    try {
      return sessionStorage.getItem(LOGOUT_FLAG_KEY) === "1";
    } catch (error) {
      return false;
    }
  };

  const redirectToKeycloakLogin = useCallback(() => {
    if (redirectingToKeycloakRef.current) {
      return;
    }
    redirectingToKeycloakRef.current = true;

    try {
      sessionStorage.removeItem(LOGOUT_FLAG_KEY);
    } catch (error) {
      // ignore
    }
    // Intentional login — do not inherit a prior logout broadcast.
    clearLogoutBroadcast();

    // Always show Keycloak login form (especially after logout).
    auth.signinRedirect({ prompt: "login" });
  }, [auth]);

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated || loggedIn || hasLogoutFlag()) {
      return;
    }

    const { email, username, profile } = parseKeycloakProfile(
      auth.user?.profile
    );

    if (email) {
      completeLogin(email, username, profile, auth.user?.access_token);
    }
  }, [
    auth.isLoading,
    auth.isAuthenticated,
    auth.user,
    loggedIn,
    completeLogin,
  ]);

  useEffect(() => {
    if (loggedIn) {
      const isFullAccess =
        (props.profile?.groups || []).includes("operations-admin") ||
        (props.profile?.groups || []).includes("/operations-admin");
      const specificAccess = props.profile?.operation_access?.length
        ? props.profile.operation_access
        : [];
      history.push(
        "/home/" +
          (isFullAccess
            ? "Tickets V2"
            : (specificAccess.length ? specificAccess : defaulAccess)[0])
      );
      return;
    }

    if (auth.isLoading || auth.error) {
      return;
    }

    // After logout (or normal unauthenticated visit), go straight to Keycloak login.
    if (hasLogoutFlag() || !auth.isAuthenticated) {
      redirectToKeycloakLogin();
    }
  }, [
    auth.isLoading,
    auth.isAuthenticated,
    loggedIn,
    auth.error,
    redirectToKeycloakLogin,
    history,
    defaulAccess,
    props.profile,
  ]);

  if (auth.error) {
    return (
      <div className={classes.login_view}>
        <p>Login failed: {auth.error.message}</p>
        <button className={classes.login_btn} onClick={redirectToKeycloakLogin}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className={classes.login_view}>
      <p>
        {auth.isAuthenticated
          ? "Signing you in..."
          : "Redirecting to Keycloak login..."}
      </p>
    </div>
  );
}

const mapStateToProps = (state) => ({
  loggedIn: state.loggedIn,
  profile: state.profile,
});

const mapDispatchToProps = (dispatch) => ({
  logIn: (payload) => dispatch({ type: actions.LOGIN, payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
